//third-party
import { allocate, multiply    } from "dinero.js";
import { LocalDate, YearMonth  } from "@js-joda/core";
import { Button, Card, Form, FormControl, InputGroup, ListGroup, Placeholder } from "react-bootstrap";
import React from "react";
import { add } from "dinero.js";

//spotsync
import { Money, showMoney, showMoneyShort, cents } from "../Data/Money";
import { showDate } from "../Data/Date";
import { ListedPlan, SubscribePreviewResp, SubscribePreviewReq } from "../Data/ApiTypes";
import { AddSubtract } from "./AddSubtract";
import { useState, useEffect } from "react";
import { Api } from "../Data/Api";
import { MonthlyPriceBreakdown } from "./MonthlyPriceBreakdown";
import { toast } from "react-toastify";
import { isPreviewProrated } from "../Data/Common";
import { OptInFeesSelector, OptInFeesSelection } from "./OptInFeesSelector";
import { DataEntitiesFinanceOperatorFeeScope } from "../shared/api/axios-client";

interface MonthlyParkingDetailsProps {
    color?:         string;
    facilityId:     number;
    start:          LocalDate;
    listedPlans:    ListedPlan[];
    selOpt:         ListedPlan;
    setSelOpt:      ( plan: ListedPlan ) => void;
    disabled?:      boolean;
    quantity?:      number;
    setQuantity?:   ( qty: number ) => void;
    /**Don't show fees or prorated pricing on preview, only on checkout details */
    isCheckoutPage: boolean; 
    isAch:          boolean;
    isPublicCheckout: boolean;
    onCouponPriceChange?: (priceId: number | null) => void;
    /**Callback when opt-in fees selection changes */
    onOptInFeesChange?: (selection: OptInFeesSelection) => void;
}

export function MonthlyParkingDetails( props: MonthlyParkingDetailsProps ) {
    const { 
        facilityId,
        start,
        listedPlans: monthlyOptions,
        selOpt,
        setSelOpt,
        quantity,
        setQuantity,
        disabled = false,
        isCheckoutPage,
        isPublicCheckout,
        onOptInFeesChange } = { ...props };

    const [isLoading, setIsLoading] = useState(true);
    const [proratedFee, setProratedFee] = useState<Money>();
    const [previewData, setPreviewData] = useState<SubscribePreviewResp | null>(null);
    const [couponCode, setCouponCode] = useState("");
    const [optInFeesSelection, setOptInFeesSelection] = useState<OptInFeesSelection>({});
    const [appliedCouponPriceId, setAppliedCouponPriceId] = useState<number | null>(null);

    // Opt-in fees handlers
    const handleOptInFeesSelectionChange = (selection: OptInFeesSelection) => {
        setOptInFeesSelection(selection);
        onOptInFeesChange?.(selection);
    };

    useEffect(() => {
        const fetchPreview = async () => {
            setIsLoading(true);
            // Get selected opt-in fee IDs
            const selectedOptInFeeIds = Object.keys(optInFeesSelection)
                .filter(id => optInFeesSelection[parseInt(id)])
                .map(id => parseInt(id));

            const req: SubscribePreviewReq = {
                planEntryId: selOpt.planEntryId,
                start,
                quantity: quantity ?? 1,
                isAch: props.isAch,
                couponPriceId: appliedCouponPriceId,
                selectedOptInFees: selectedOptInFeeIds.length > 0 ? selectedOptInFeeIds : undefined
            };
            const result = await Api.subscriptionPreview(selOpt.planId, req);
            
            if (result.isOk) {
                setPreviewData(result.value);
                setProratedFee(result.value.price);
            }
            setIsLoading(false);
        };

        fetchPreview();
    }, [selOpt, quantity, appliedCouponPriceId, optInFeesSelection]);

    useEffect(() => {
    setCouponCode("");
    setAppliedCouponPriceId(null);
    props.onCouponPriceChange?.(null);
    }, [selOpt]);


    const monthlyRate = multiply(selOpt.price, quantity ?? 1);

    async function handleApplyCoupon() { 
        if (!couponCode.trim()) return;

        try {
            const res = await Api.applyCouponToPlan(selOpt.planId, couponCode);

            if (res?.isOk && res.value) {
                setAppliedCouponPriceId(res.value.priceId);
                props.onCouponPriceChange?.(res.value.priceId);
                toast.success("Coupon applied successfully");
            } else {
                setCouponCode("");
                setAppliedCouponPriceId(null);
                props.onCouponPriceChange?.(null)
                toast.error("Sorry, the coupon code was not found");
            }
        } catch (err) {
            setCouponCode("");
            props.onCouponPriceChange?.(null)
            setAppliedCouponPriceId(null);
            toast.error("Sorry, the coupon code was not found");
        }
    }   

    return <Card className='border-2 border-primary my-2 shadow'>
        <Card.Header className='text-center header-gradient'>
            Monthly Parking Options
        </Card.Header>
        <Card.Body>
            {monthlyOptions.map( listedPlan =>
                <ListGroup.Item key={listedPlan.planEntryId}>
                    <div className={`d-flex flex-row border rounded-4 p-3 m-2 fs-7 ${listedPlan === selOpt ? 'border-primary' : ''}`}
                        onClick={() => !disabled && setSelOpt( listedPlan )}>
                        <Form.Check
                            className="m-0 flex-grow-1"
                            type="radio"
                            label={`${listedPlan.name}`}
                            checked={listedPlan === selOpt}
                            inline
                            readOnly />
                        <span className="fw-bold">
                            {showMoneyShort( listedPlan.price )}
                        </span>
                    </div>
                </ListGroup.Item> )}
                {isCheckoutPage && isPublicCheckout && (
                <div className="mt-3 mb-2">
                    <InputGroup>
                    <FormControl
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        disabled={appliedCouponPriceId != null || disabled} 
                    />
                    {appliedCouponPriceId == null ? (
                        <Button
                        onClick={handleApplyCoupon}
                        variant="primary"
                        >
                        Apply
                        </Button>
                    ) : (
                        <Button
                        onClick={() => {
                            setCouponCode("");
                            setAppliedCouponPriceId(null);
                            props.onCouponPriceChange?.(null); 
                            toast.info("Coupon removed");
                        }}
                        variant="secondary"
                        >
                        Reset
                        </Button>
                    )}
                    </InputGroup>
                </div>
                )}

                {appliedCouponPriceId != null && (
                <span className="badge bg-success fs-6">
                    Coupon Applied
                </span>
                )}
                   
            <div className="mt-2 w-100 gap-3" style={{ display: "inline-grid", gridTemplateColumns: "auto 1fr 1fr" }}>
                {( quantity !== undefined && setQuantity !== undefined ) && <>
                    <div className="text-start d-flex align-items-center">
                        Spots
                    </div>
                    <div></div>
                    <div className="text-end">
                        <div className="d-inline-flex align-items-center justify-content-start justify-content-md-center gap-1">
                            <AddSubtract quantity={quantity} set={ qty => disabled !== true && setQuantity( qty ) } />
                        </div>
                    </div>
                </>}

                <div className="text-start fw-bold" style={{ gridColumn: "1 / 4" }}></div>

                <div style={{ marginTop: "-20px" }} className="text-start">Start Date</div> <div></div>
                <div style={{ marginTop: "-20px" }} className="text-end">{showDate(start)}</div>
             
                <div className="text-start">Monthly Rate</div>
                <div></div>
                <div className="text-end fw-bold">
                {previewData 
                    ? showMoney(previewData.fullUnitPrice) 
                    : showMoney(monthlyRate)}
                </div>

                {/* Opt-in fees selector */}
                {isCheckoutPage && (
                    <div style={{ gridColumn: "1 / 4" }}>
                        <OptInFeesSelector
                            disabled={disabled}
                            facilityId={facilityId}
                            feeScope={DataEntitiesFinanceOperatorFeeScope.NUMBER_1} // 1 for monthly parking
                            feePaymentType={props.isAch ? 1 : 0} // ACH vs Card payment type
                            onSelectionChange={handleOptInFeesSelectionChange}
                            isCheckout={isCheckoutPage}
                        />
                    </div>
                )}

                {isCheckoutPage && (
                    <div style={{ gridColumn: "1 / 4", width: "100%" }}>
                        {isLoading ? (
                            <div className="border rounded p-2">
                                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.5rem" }}>
                                    <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={6} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={4} /></Placeholder>
                                    <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={4} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                                    <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={5} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                                </div>
                            </div>
                        ) : previewData && (
                            <div className="fade-in">
                                <MonthlyPriceBreakdown 
                                    price={previewData.price}
                                    fees={previewData.fees}
                                    showTotalLabel="Due Today"
                                    showProratedHint={isPreviewProrated(previewData)}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Card.Body>
    </Card>;
}